import React from "react";

import { OutlinedInput, Button } from "@material-ui/core";
import ReplayIcon from "@material-ui/icons/Replay";

import { ErrorText } from "../../../widgets";
import style from "../style.module.css";
import { onboarding_constants } from "constants/onboardingConstant";
import classnames from "classnames";
import { getRegexObjectFromString } from "ui/pages/TemplatePreview/utils";
import { SUBDOMAIN_VALIDATION_REGEX_PATTERN } from "constants/regExpPatterns";
import { SUBDOMAIN_VALIDATION_ERROR_MSG } from "constants/displayStrings";
import { getCreatorDomain } from "utils/AuthUtil";
import { WhiteLabeledFooter } from "features/WhiteLabeledFooter/WhiteLabeledFooter";
import {
  UNLOCK_WHITELABELED_MESSAGES,
  WHITELABELED_DOMAIN_LINK,
} from "features/WhiteLabeledFooter/constants/WhiteLabeledFooter.constants";
import { useWhitelabel } from "features/Account/modules/Whitelabel/utils/useWhitelabel";

export const EditSubdomain = (props) => {
  const {
    state,
    initialState,
    setField,
    errorState,
    setErrorMessage,
    debounce,
    checkSubdomainValid,
    errorHandlerToggle,
    disableAllEdits,
  } = props;

  const { isAccessible } = useWhitelabel();

  const DomainInfoCard = ({ description, linkLabel, linkTo, domainClass }) => (
    <div
      className={classnames("tw-flex tw-gap-1 tw-items-center", domainClass)}
    >
      <div className="tw-font-medium tw-text-[14px] tw-leading-[100%] tw-tracking-[0.02em] tw-flex tw-flex-col tw-gap-1">
        <div className="tw-font-semibold tw-text-[16px] tw-leading-[21px] tw-tracking-[0.04em] tw-align-middle">
          Custom Domain
        </div>
        <div>
          {description}&nbsp;
          <span
            className="tw-underline tw-text-[#493ab1] tw-cursor-pointer"
            onClick={() => window.location.assign(linkTo)}
          >
            {linkLabel}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div className={style.fieldContainer}>
      <div className={classnames(style.info, "pb-3")}>
        The URL where your website is accessible. This can be changed anytime.
      </div>
      <h1 className="tw-text-[14px] tw-font-medium tw-text-[#272522]">
        Enter your webpage address
      </h1>
      <div className={`${style.field} ${style.subDomainField}`}>
        <OutlinedInput
          fullWidth
          disabled={disableAllEdits}
          inputProps={{ maxLength: onboarding_constants.sub_domain.maxLength }} // stop user as soon as the limit is reached
          onChange={async (e) => {
            const inputVal = `${(e.target.value || "")
              .trim()
              .split(" ")
              .join("")
              .toLocaleLowerCase()}`;
            setField("sub_domain", inputVal);

            if (initialState?.sub_domain === inputVal) {
              setErrorMessage("sub_domain", false, null);
              return;
            }
            // Only allow chars and numbers and hyphens between characters/numbers and
            // no other special characters
            const isValidSubDomain = getRegexObjectFromString(
              SUBDOMAIN_VALIDATION_REGEX_PATTERN,
              "g"
            ).test(inputVal);

            if (isValidSubDomain) {
              // handleSubdomainChange(e.target.value);
              if (inputVal.length > onboarding_constants.sub_domain.maxLength) {
                setErrorMessage(
                  "sub_domain",
                  `Cannot exceed ${onboarding_constants.sub_domain.maxLength} characters. (Currently ${inputVal.length})`
                );
                return;
              }

              if (inputVal.length < 2) {
                setErrorMessage("sub_domain", "Must be at least 2 characters");
                return;
              }
              setErrorMessage(
                "sub_domain",
                "Checking for subdomain validity...",
                { isInfo: true }
              );

              debounce(async () => {
                const { isAvailable: isValid, message } =
                  await checkSubdomainValid(inputVal);
                setErrorMessage(
                  "sub_domain",
                  isValid ? "Subdomain available" : message,
                  {
                    isSuccess: isValid ? "success" : null,
                  }
                );
              });
            } else {
              setErrorMessage("sub_domain", SUBDOMAIN_VALIDATION_ERROR_MSG);
            }
          }}
          value={state.sub_domain}
          className={`${style.input} ${style.subDomainInput}`}
          {...errorHandlerToggle("sub_domain", {
            noFocus: true,
          })}
        />
      </div>
      <div className="tw-text-[12px] tw-mb-[6px] tw-text-[#27252270]">
        https://
        <span className="tw-text-[#272522] tw-font-medium">
          {state.sub_domain || "your-name"}
        </span>
        {""}.{getCreatorDomain()}
      </div>
      <div className={style.whiteLabelDomain}>
        {isAccessible ? (
          getCreatorDomain() === process.env.REACT_APP_PROJECT_DOMAIN ? (
            <DomainInfoCard
              description={WHITELABELED_DOMAIN_LINK.DISCONNECTED.description}
              linkLabel={WHITELABELED_DOMAIN_LINK.DISCONNECTED.linkLabel}
              linkTo={WHITELABELED_DOMAIN_LINK.DISCONNECTED.link}
              domainClass="tw-bg-[#fff1d6] tw-p-2.5 tw-h-16 !tw-border !tw-border-solid !tw-border-1.5 !tw-border-[#f7ad1e] tw-flex tw-justify-between tw-rounded-md tw-bottom-5"
            />
          ) : (
            <DomainInfoCard
              description={WHITELABELED_DOMAIN_LINK.CONNECTED.description}
              linkLabel={WHITELABELED_DOMAIN_LINK.CONNECTED.linkLabel}
              linkTo={WHITELABELED_DOMAIN_LINK.CONNECTED.link}
              domainClass="tw-bg-[#d4fff0] !tw-border !tw-border-solid !tw-border-1.5 !tw-border-[#03b77a] tw-p-2.5 tw-h-16 tw-flex tw-justify-between tw-rounded-md"
            />
          )
        ) : (
          <WhiteLabeledFooter
            textLabel={WHITELABELED_DOMAIN_LINK.PURCHASE_MESSAGE}
            className="!tw-w-auto"
            showNoButton={false}
            whiteLabelModalHeading={
              UNLOCK_WHITELABELED_MESSAGES.HOME_PAGE_MESSAGE
            }
          />
        )}
      </div>
      <ErrorText errorState={errorState} fieldKey="sub_domain" />
      {initialState?.sub_domain &&
      initialState.sub_domain !== state.sub_domain ? (
        <div className={style.resetButtonContainer}>
          <Button
            color="primary"
            title="Reset subdomain to old value"
            className={`${style.button} ${style.resetButton}`}
            onClick={() => {
              setField("sub_domain", initialState?.sub_domain);
              setErrorMessage("sub_domain", null);
            }}
            disabled={disableAllEdits}
          >
            <ReplayIcon />
            Reset Subdomain
          </Button>
        </div>
      ) : null}
    </div>
  );
};
